// services/SyncService.js with cultivation sync added
import offlineDBService from './dexieDB';
import { FileUploader } from 'api/endpoints/DigitizationUtility';
import { farmerReportApi } from 'api/endpoints/farmers';
import { farmReportApi } from 'api/endpoints/farm';
import { cultivationApi } from 'api/endpoints/Cultivation'; // Import the cultivation API
import {
  EventEmitter,
  GLOBAL_EVENTS,
  globalEventBus
} from '../utils/eventEmitter';

// Create sync specific events emitter
export const syncEvents = new EventEmitter();

// Define sync event types - Add cultivation synced event
export const SYNC_EVENTS = {
  STARTED: 'sync:started',
  PROGRESS: 'sync:progress',
  COMPLETED: 'sync:completed',
  FAILED: 'sync:failed',
  FARMER_SYNCED: 'sync:farmer:synced',
  FARM_SYNCED: 'sync:farm:synced',
  CULTIVATION_SYNCED: 'sync:cultivation:synced', // New event for cultivation sync
  AUTH_ERROR: 'sync:auth:error',
  FILE_UPLOADED: 'sync:file:uploaded'
};

/**
 * Helper function to check if an error is an authentication error (401)
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether it's an auth error
 */
export const isAuthError = error => {
  return (
    error?.response?.status === 401 ||
    error?.status === 401 ||
    (typeof error?.message === 'string' &&
      (error.message.includes('unauthorized') ||
        error.message.includes('not authorized') ||
        error.message.includes('authentication failed') ||
        error.message.includes('token expired') ||
        error.message.includes('invalid token')))
  );
};

/**
 * Helper function to convert data URL to File object
 * @param {string} dataURL - Data URL
 * @param {string} fileName - File name
 * @param {string} fileType - File type
 * @returns {File} - File object
 */
export const dataURLtoFile = (dataURL, fileName, fileType) => {
  const arr = dataURL.split(',');
  const mime = fileType || arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
};

/**
 * Show notification based on sync results
 * @param {Object} result - Sync result
 */
export const showSyncNotification = result => {
  const { successCount = 0, failCount = 0 } = result;

  if (successCount > 0 || failCount > 0) {
    let message = '';

    if (failCount === 0 && successCount > 0) {
      message = `Successfully synced ${successCount} items`;
    } else if (successCount === 0 && failCount > 0) {
      message = `Failed to sync ${failCount} items`;
    } else {
      message = `Synced ${successCount} items, ${failCount} failed`;
    }

    // Add notification info to sync event
    return {
      message,
      type:
        failCount === 0
          ? 'success'
          : failCount > 0 && successCount > 0
          ? 'warning'
          : 'error'
    };
  } else {
    return {
      message: 'No items to sync',
      type: 'info'
    };
  }
};

/**
 * Synchronize offline data with the server
 * @param {string} tenant - Tenant ID
 * @returns {Promise<Object>} - Sync result
 */
export const syncData = async tenant => {
  try {
    // Emit sync started event
    syncEvents.emit(SYNC_EVENTS.STARTED);
    // Also emit to global event bus
    globalEventBus.emit(GLOBAL_EVENTS.SYNC_STARTED);

    // Get unsynced data at the start
    const unsyncedData = await offlineDBService.getUnsyncedData();
    console.log('Starting sync with unsynced data:', {
      farmersCount: unsyncedData.farmers?.length || 0,
      farmsCount: unsyncedData.farms?.length || 0,
      formsCount: unsyncedData.forms?.length || 0,
      cultivationsCount: unsyncedData.cultivations?.length || 0
    });

    // If there's nothing to sync, return immediately
    if (
      !unsyncedData.farmers?.length &&
      !unsyncedData.farms?.length &&
      !unsyncedData.forms?.length &&
      !unsyncedData.cultivations?.length
    ) {
      console.log('No data to sync, returning early');

      const result = {
        success: true,
        successCount: 0,
        failCount: 0,
        stillHasUnsyncedData: false
      };

      // Emit completed event
      syncEvents.emit(SYNC_EVENTS.COMPLETED, result);
      globalEventBus.emit(GLOBAL_EVENTS.SYNC_COMPLETED, result);

      return result;
    }

    // Get form definitions for file uploads
    const farmerFormDef = await offlineDBService.getFormDefinition(
      'farmer-create'
    );
    const farmFormDef = await offlineDBService.getFormDefinition('farm-create');
    const cultivationFormDef = await offlineDBService.getFormDefinition(
      'cultivation-create'
    );

    // Track sync results
    let successCount = 0;
    let failCount = 0;
    let authErrorOccurred = false;

    // Create maps to store ID mappings
    const farmerIdMappings = new Map();
    const farmIdMappings = new Map(); // New map for farm ID mappings

    //===========================================================================
    // PHASE 1: SYNC ALL FARMERS FIRST
    //===========================================================================
    console.log('PHASE 1: SYNCING FARMERS FIRST');
    syncEvents.emit(SYNC_EVENTS.PROGRESS, {
      phase: 1,
      message: 'Syncing farmers',
      total: unsyncedData.farmers.length,
      current: 0
    });

    // Process all farmers
    for (let i = 0; i < unsyncedData.farmers.length; i++) {
      const farmer = unsyncedData.farmers[i];

      syncEvents.emit(SYNC_EVENTS.PROGRESS, {
        phase: 1,
        message: `Syncing farmer ${i + 1} of ${unsyncedData.farmers.length}`,
        total: unsyncedData.farmers.length,
        current: i
      });

      try {
        // If we've already encountered an auth error, don't try to process more items
        if (authErrorOccurred) break;

        // Make a copy of the farmer data
        const farmerToSync = { ...farmer };

        // Check for file references
        const fileFields = [];
        for (const key in farmerToSync) {
          const value = farmerToSync[key];
          if (typeof value === 'string' && value.startsWith('file_')) {
            fileFields.push({ field: key, fileKey: value });
          }
        }

        // Process files if any
        if (fileFields.length > 0) {
          for (const { field, fileKey } of fileFields) {
            try {
              // Get file from DexieDB
              const file = await offlineDBService.getFile(fileKey);

              if (!file) continue;

              // Get upload config from form definition
              const fieldConfig = Array.isArray(farmerFormDef?.formData)
                ? farmerFormDef.formData.find(f => f.id === field)
                : null;

              // Convert to File object
              const fileObj = dataURLtoFile(
                file.dataUrl,
                file.fileName,
                file.fileType
              );

              // Create fake event
              const fakeEvent = { target: { files: [fileObj] } };

              // Create upload metadata
              const uploadMetadata = {
                id: field,
                uploadConfig: fieldConfig?.uploadConfig || {
                  maxFileSizeMB: 5,
                  uploadEndpoint: '/utility/upload/image'
                }
              };

              // Upload file
              try {
                const uploadResult = await FileUploader(
                  fakeEvent,
                  uploadMetadata
                );

                // Update farmer data with file URL
                if (uploadResult && uploadResult.file) {
                  farmerToSync[field] = uploadResult.file;

                  // Emit file uploaded event
                  syncEvents.emit(SYNC_EVENTS.FILE_UPLOADED, {
                    entityType: 'farmer',
                    entityId: farmer.id,
                    field,
                    fileKey,
                    uploadedUrl: uploadResult.file
                  });
                }
              } catch (uploadError) {
                console.error(`Error uploading file:`, uploadError);

                // Check for auth error during file upload
                if (isAuthError(uploadError)) {
                  authErrorOccurred = true;
                  syncEvents.emit(SYNC_EVENTS.AUTH_ERROR, {
                    error: uploadError
                  });
                  break;
                }
              }
            } catch (fileError) {
              console.error(`Error processing file:`, fileError);
            }
          }
        }

        // If auth error occurred during file processing, break out
        if (authErrorOccurred) break;

        try {
          // Log the farmer data we're trying to sync for debugging
          console.log(`Attempting to sync farmer ${farmer.id}:`, farmerToSync);

          const response = await farmerReportApi.sendFarmerCreate(
            tenant,
            farmerToSync
          );

          // Log the raw response for debugging
          console.log(`API response for farmer ${farmer.id}:`, response);

          // Success check
          const isSuccessful =
            response &&
            (response.success === true ||
              (response.status >= 200 &&
                response.status < 300 &&
                !response.message &&
                !response.error));

          console.log(
            `Sync success check for farmer ${farmer.id}: ${isSuccessful}`
          );

          if (isSuccessful) {
            // Extract the permanent ID from the response
            const permanentFarmerId =
              response.data?.id ||
              response.data?.farmer_id ||
              response.id ||
              response.farmerId;

            console.log(`Permanent farmer ID from API: ${permanentFarmerId}`);

            // Store mapping from temp ID to permanent ID if they are different
            if (permanentFarmerId && permanentFarmerId !== farmer.id) {
              console.log(
                `Mapping temp farmer ID ${farmer.id} to permanent ID ${permanentFarmerId}`
              );
              farmerIdMappings.set(
                farmer.id.toString(),
                permanentFarmerId.toString()
              );
            }

            // Only if successful, delete from local DB and associated files
            try {
              // Mark as synced in database AND remove from database
              const deleteResult = await offlineDBService.markAsSyncedAndRemove(
                'farmers',
                farmer.id
              );

              if (deleteResult) {
                console.log(
                  `Removed farmer ${farmer.id} from local DB after successful sync`
                );

                // Delete files only after successful sync
                for (const { fileKey } of fileFields) {
                  try {
                    await offlineDBService.deleteFile(fileKey);
                  } catch (error) {
                    console.error(`Error deleting file:`, error);
                  }
                }

                successCount++;

                // Emit farmer synced event
                syncEvents.emit(SYNC_EVENTS.FARMER_SYNCED, {
                  tempId: farmer.id,
                  permanentId: permanentFarmerId,
                  success: true
                });
              } else {
                console.error(
                  `Failed to delete farmer ${farmer.id} from database, marking as sync failure`
                );
                failCount++;
              }
            } catch (deleteError) {
              console.error(
                `Error deleting farmer ${farmer.id} after successful sync:`,
                deleteError
              );
              // If we can't delete, count as failure because data is still in local DB
              failCount++;
            }
          } else {
            // Check for error message
            const errorMessage =
              response?.message ||
              response?.error ||
              response?.errorMessage ||
              (response?.data && response.data.message) ||
              'Unknown error';

            console.error(
              `Failed to sync farmer ${farmer.id}: ${errorMessage}`
            );

            // Update with error info but DON'T delete from database
            await offlineDBService.updateFarmerSyncError(
              farmer.id,
              errorMessage
            );
            failCount++;

            // Emit farmer synced event with failure
            syncEvents.emit(SYNC_EVENTS.FARMER_SYNCED, {
              tempId: farmer.id,
              success: false,
              error: errorMessage
            });
          }
        } catch (apiError) {
          console.error(`API error syncing farmer:`, apiError);

          // Check if this is an authentication error
          if (isAuthError(apiError)) {
            authErrorOccurred = true;
            syncEvents.emit(SYNC_EVENTS.AUTH_ERROR, { error: apiError });
            break;
          }

          // Extract error message from API error
          const errorMessage =
            apiError.response?.data?.desc ||
            apiError.response?.data?.message ||
            apiError.message ||
            'API error';

          // Otherwise handle as regular error - update but DON'T delete
          await offlineDBService.updateFarmerSyncError(farmer.id, errorMessage);
          failCount++;

          // Emit farmer synced event with failure
          syncEvents.emit(SYNC_EVENTS.FARMER_SYNCED, {
            tempId: farmer.id,
            success: false,
            error: errorMessage
          });
        }
      } catch (error) {
        console.error(`Error syncing farmer:`, error);

        // Try to update the error info in the database
        try {
          await offlineDBService.updateFarmerSyncError(
            farmer.id,
            error.message || 'Unexpected error'
          );
        } catch (logError) {
          console.error(
            `Failed to log error for farmer ${farmer.id}:`,
            logError
          );
        }

        failCount++;

        // Emit farmer synced event with failure
        syncEvents.emit(SYNC_EVENTS.FARMER_SYNCED, {
          tempId: farmer.id,
          success: false,
          error: error.message || 'Unexpected error'
        });
      }
    }

    // Log the farmer ID mappings for debugging
    console.log('Farmer ID mappings:', Object.fromEntries(farmerIdMappings));

    // Early exit if auth error occurred - we don't want to try syncing farms
    if (authErrorOccurred) {
      console.log('Auth error occurred during farmer sync, skipping farm sync');

      // Check remaining unsynced data
      const remainingUnsyncedData = await offlineDBService.getUnsyncedData();
      const stillHasUnsyncedData =
        remainingUnsyncedData.farmers?.length > 0 ||
        remainingUnsyncedData.farms?.length > 0 ||
        remainingUnsyncedData.forms?.length > 0 ||
        remainingUnsyncedData.cultivations?.length > 0;

      const result = {
        success: false,
        error: 'Authentication error',
        successCount,
        failCount,
        stillHasUnsyncedData,
        countsByType: {
          farmers: remainingUnsyncedData.farmers?.length || 0,
          farms: remainingUnsyncedData.farms?.length || 0,
          forms: remainingUnsyncedData.forms?.length || 0,
          cultivations: remainingUnsyncedData.cultivations?.length || 0
        }
      };

      // Emit sync failed events
      syncEvents.emit(SYNC_EVENTS.FAILED, result);
      globalEventBus.emit(GLOBAL_EVENTS.SYNC_FAILED, result);

      return result;
    }

    //===========================================================================
    // PHASE 2: UPDATE ALL FARM RECORDS WITH PERMANENT FARMER IDs
    //===========================================================================
    console.log('PHASE 2: UPDATING FARM RECORDS WITH PERMANENT FARMER IDs');
    syncEvents.emit(SYNC_EVENTS.PROGRESS, {
      phase: 2,
      message: 'Updating farm records with permanent farmer IDs',
      total: 1,
      current: 0
    });

    // Only proceed if we have ID mappings
    if (farmerIdMappings.size > 0) {
      // Get all unsynced farms
      const farmsToUpdate = await offlineDBService.getUnsyncedData();

      // Update any farms that have temporary farmer IDs
      for (const farm of farmsToUpdate.farms) {
        // Check if the 'farmer' field has a temporary ID that needs updating
        if (farm.farmer && farmerIdMappings.has(farm.farmer.toString())) {
          const permanentFarmerId = farmerIdMappings.get(
            farm.farmer.toString()
          );
          console.log(
            `Updating farm ${farm.id} with permanent farmer ID ${permanentFarmerId} (was: ${farm.farmer})`
          );

          // Use the helper method to update the farmer ID
          await offlineDBService.updateFarmFarmerId(farm.id, permanentFarmerId);
        }
      }

      console.log('Completed updating farm records with permanent farmer IDs');
    } else {
      console.log('No farmer ID mappings found, no farm updates needed');
    }

    syncEvents.emit(SYNC_EVENTS.PROGRESS, {
      phase: 2,
      message: 'Updated farm records with permanent farmer IDs',
      total: 1,
      current: 1
    });

    //===========================================================================
    // PHASE 3: SYNC FARMS (NOW WITH PERMANENT FARMER IDs)
    //===========================================================================
    console.log('PHASE 3: SYNCING FARMS WITH PERMANENT FARMER IDs');

    // Get fresh farm data after updating farmerId values
    const refreshedFarms = await offlineDBService.getUnsyncedData();
    console.log(
      `Processing ${refreshedFarms.farms.length} unsynced farms after farmer sync`
    );

    syncEvents.emit(SYNC_EVENTS.PROGRESS, {
      phase: 3,
      message: 'Syncing farms',
      total: refreshedFarms.farms.length,
      current: 0
    });

    // Process farms
    for (let i = 0; i < refreshedFarms.farms.length; i++) {
      const farm = refreshedFarms.farms[i];

      syncEvents.emit(SYNC_EVENTS.PROGRESS, {
        phase: 3,
        message: `Syncing farm ${i + 1} of ${refreshedFarms.farms.length}`,
        total: refreshedFarms.farms.length,
        current: i
      });

      try {
        // Make a copy of the farm data
        const farmToSync = { ...farm };

        // Double check: If this farm still has a temp ID, apply the mapping
        // This is a fallback in case the database update in Phase 2 failed
        if (
          farmToSync.farmer &&
          farmerIdMappings.has(farmToSync.farmer.toString())
        ) {
          const permanentFarmerId = farmerIdMappings.get(
            farmToSync.farmer.toString()
          );
          console.log(
            `Runtime check: Using permanent farmer ID ${permanentFarmerId} instead of temp ID ${farmToSync.farmer} for farm ${farm.id}`
          );
          farmToSync.farmer = permanentFarmerId;
        }

        // Check for file references
        const fileFields = [];
        for (const key in farmToSync) {
          const value = farmToSync[key];
          if (typeof value === 'string' && value.startsWith('file_')) {
            fileFields.push({ field: key, fileKey: value });
          }
        }

        // Process files if any
        if (fileFields.length > 0) {
          for (const { field, fileKey } of fileFields) {
            try {
              // Get file from DexieDB
              const file = await offlineDBService.getFile(fileKey);

              if (!file) continue;

              // Get upload config from form definition
              const fieldConfig = Array.isArray(farmFormDef?.formData)
                ? farmFormDef.formData.find(f => f.id === field)
                : null;

              // Convert to File object
              const fileObj = dataURLtoFile(
                file.dataUrl,
                file.fileName,
                file.fileType
              );

              // Create fake event
              const fakeEvent = { target: { files: [fileObj] } };

              // Create upload metadata
              const uploadMetadata = {
                id: field,
                uploadConfig: fieldConfig?.uploadConfig || {
                  maxFileSizeMB: 5,
                  uploadEndpoint: '/utility/upload/image'
                }
              };

              // Upload file
              try {
                const uploadResult = await FileUploader(
                  fakeEvent,
                  uploadMetadata
                );

                // Update farm data with file URL
                if (uploadResult && uploadResult.file) {
                  farmToSync[field] = uploadResult.file;

                  // Emit file uploaded event
                  syncEvents.emit(SYNC_EVENTS.FILE_UPLOADED, {
                    entityType: 'farm',
                    entityId: farm.id,
                    field,
                    fileKey,
                    uploadedUrl: uploadResult.file
                  });
                }
              } catch (uploadError) {
                console.error(`Error uploading file:`, uploadError);

                // Check for auth error during file upload
                if (isAuthError(uploadError)) {
                  authErrorOccurred = true;
                  syncEvents.emit(SYNC_EVENTS.AUTH_ERROR, {
                    error: uploadError
                  });
                  break;
                }
              }
            } catch (fileError) {
              console.error(`Error processing file:`, fileError);
            }
          }
        }

        // If auth error occurred during file processing, break out
        if (authErrorOccurred) break;

        try {
          // Log the farm data we're trying to sync for debugging
          console.log(`Attempting to sync farm ${farm.id}:`, farmToSync);

          // Make sure to pass the correct farmerId (which should now be permanent)
          const response = await farmReportApi.submitFarm(
            farmToSync.farmer,
            farmToSync
          );

          // Log the raw response for debugging
          console.log(`API response for farm ${farm.id}:`, response);

          // Success check - similar to farmer sync
          const isSuccessful =
            response &&
            (response.success === true ||
              (response.status >= 200 &&
                response.status < 300 &&
                !response.message &&
                !response.error));

          // Log the result of our success check
          console.log(
            `Sync success check for farm ${farm.id}: ${isSuccessful}`
          );

          if (isSuccessful) {
            // Get the permanent farm ID from the API response
            const permanentFarmId =
              response.data?.id ||
              response.data?.farm_id ||
              response.id ||
              response.farmId;

            // Store farm ID mapping if there's a difference between temp and permanent IDs
            if (permanentFarmId && permanentFarmId !== farm.id) {
              console.log(
                `Mapping temp farm ID ${farm.id} to permanent ID ${permanentFarmId}`
              );
              farmIdMappings.set(
                farm.id.toString(),
                permanentFarmId.toString()
              );
            }

            // Only if successful, delete from local DB and associated files
            try {
              // Mark as synced in database AND remove from database
              const deleteResult = await offlineDBService.markAsSyncedAndRemove(
                'farms',
                farm.id
              );

              if (deleteResult) {
                console.log(
                  `Removed farm ${farm.id} from local DB after successful sync`
                );

                // Delete files only after successful sync
                for (const { fileKey } of fileFields) {
                  try {
                    await offlineDBService.deleteFile(fileKey);
                  } catch (error) {
                    console.error(`Error deleting file:`, error);
                  }
                }

                successCount++;

                // Emit farm synced event
                syncEvents.emit(SYNC_EVENTS.FARM_SYNCED, {
                  id: farm.id,
                  farmerId: farmToSync.farmer,
                  permanentId: permanentFarmId || farm.id,
                  success: true
                });
              } else {
                console.error(
                  `Failed to delete farm ${farm.id} from database, marking as sync failure`
                );
                failCount++;

                // Emit farm synced event with failure
                syncEvents.emit(SYNC_EVENTS.FARM_SYNCED, {
                  id: farm.id,
                  farmerId: farmToSync.farmer,
                  success: false,
                  error: 'Failed to delete from local database'
                });
              }
            } catch (deleteError) {
              console.error(
                `Error deleting farm ${farm.id} after successful sync:`,
                deleteError
              );
              // If we can't delete, count as failure because data is still in local DB
              failCount++;

              // Emit farm synced event with failure
              syncEvents.emit(SYNC_EVENTS.FARM_SYNCED, {
                id: farm.id,
                farmerId: farmToSync.farmer,
                success: false,
                error:
                  deleteError.message || 'Error deleting from local database'
              });
            }
          } else {
            // Handle error response
            const errorMessage =
              response?.message ||
              response?.error ||
              response?.errorMessage ||
              (response?.data && response.data.message) ||
              'Unknown error';

            console.error(`Failed to sync farm ${farm.id}: ${errorMessage}`);

            // Update farm record with error info
            await offlineDBService.updateFarmSyncError(farm.id, errorMessage);

            failCount++;

            // Emit farm synced event with failure
            syncEvents.emit(SYNC_EVENTS.FARM_SYNCED, {
              id: farm.id,
              farmerId: farmToSync.farmer,
              success: false,
              error: errorMessage
            });
          }
        } catch (apiError) {
          console.error(`API error syncing farm:`, apiError);

          // Check if this is an authentication error
          if (isAuthError(apiError)) {
            authErrorOccurred = true;
            syncEvents.emit(SYNC_EVENTS.AUTH_ERROR, { error: apiError });
            break;
          }

          // Extract error message from API error
          const errorMessage =
            apiError.response?.data?.desc ||
            apiError.response?.data?.message ||
            apiError.message ||
            'API error';

          // Update error info
          await offlineDBService.updateFarmSyncError(farm.id, errorMessage);

          failCount++;

          // Emit farm synced event with failure
          syncEvents.emit(SYNC_EVENTS.FARM_SYNCED, {
            id: farm.id,
            farmerId: farmToSync.farmer,
            success: false,
            error: errorMessage
          });
        }
      } catch (error) {
        console.error(`Error syncing farm:`, error);
        failCount++;

        // Emit farm synced event with failure
        syncEvents.emit(SYNC_EVENTS.FARM_SYNCED, {
          id: farm.id,
          success: false,
          error: error.message || 'Unexpected error'
        });
      }
    }

    // Log the farm ID mappings for debugging
    console.log('Farm ID mappings:', Object.fromEntries(farmIdMappings));

    //===========================================================================
    // PHASE 4: UPDATE CULTIVATION RECORDS WITH PERMANENT FARM IDs
    //===========================================================================
    console.log(
      'PHASE 4: UPDATING CULTIVATION RECORDS WITH PERMANENT FARM IDs'
    );
    syncEvents.emit(SYNC_EVENTS.PROGRESS, {
      phase: 4,
      message: 'Updating cultivation records with permanent farm IDs',
      total: 1,
      current: 0
    });

    // Only proceed if we have farm ID mappings
    if (farmIdMappings.size > 0) {
      // Get all unsynced cultivations
      const cultivationsToUpdate = await offlineDBService.getUnsyncedData();

      // Update any cultivations that have temporary farm IDs
      for (const cultivation of cultivationsToUpdate.cultivations || []) {
        // Check if the 'farm' field has a temporary ID that needs updating
        if (
          cultivation.farm &&
          farmIdMappings.has(cultivation.farm.toString())
        ) {
          const permanentFarmId = farmIdMappings.get(
            cultivation.farm.toString()
          );
          console.log(
            `Updating cultivation ${cultivation.id} with permanent farm ID ${permanentFarmId} (was: ${cultivation.farm})`
          );

          // Update the farm ID in the cultivation record
          // Create a new helper method in offlineDBService or use existing updateCultivation method
          await offlineDBService.updateCultivation(cultivation.id, {
            ...cultivation,
            farm: permanentFarmId
          });
        }
      }

      console.log(
        'Completed updating cultivation records with permanent farm IDs'
      );
    } else {
      console.log('No farm ID mappings found, no cultivation updates needed');
    }

    syncEvents.emit(SYNC_EVENTS.PROGRESS, {
      phase: 4,
      message: 'Updated cultivation records with permanent farm IDs',
      total: 1,
      current: 1
    });

    //===========================================================================
    // PHASE 5: SYNC CULTIVATIONS (NOW WITH PERMANENT FARM IDs)
    //===========================================================================
    console.log('PHASE 5: SYNCING CULTIVATIONS WITH PERMANENT FARM IDs');

    // Get fresh cultivation data after updating farm IDs
    const refreshedData = await offlineDBService.getUnsyncedData();
    const cultivationsToSync = refreshedData.cultivations || [];

    console.log(
      `Processing ${cultivationsToSync.length} unsynced cultivations after farm sync`
    );

    syncEvents.emit(SYNC_EVENTS.PROGRESS, {
      phase: 5,
      message: 'Syncing cultivations',
      total: cultivationsToSync.length,
      current: 0
    });

    // Process cultivations
    for (let i = 0; i < cultivationsToSync.length; i++) {
      const cultivation = cultivationsToSync[i];

      syncEvents.emit(SYNC_EVENTS.PROGRESS, {
        phase: 5,
        message: `Syncing cultivation ${i + 1} of ${cultivationsToSync.length}`,
        total: cultivationsToSync.length,
        current: i
      });

      try {
        // Make a copy of the cultivation data
        const cultivationToSync = { ...cultivation };

        // Double check: If this cultivation still has a temp farm ID, apply the mapping
        // This is a fallback in case the database update in Phase 4 failed
        if (
          cultivationToSync.farm &&
          farmIdMappings.has(cultivationToSync.farm.toString())
        ) {
          const permanentFarmId = farmIdMappings.get(
            cultivationToSync.farm.toString()
          );
          console.log(
            `Runtime check: Using permanent farm ID ${permanentFarmId} instead of temp ID ${cultivationToSync.farm} for cultivation ${cultivation.id}`
          );
          cultivationToSync.farm = permanentFarmId;
        }

        // Check for file references
        const fileFields = [];
        for (const key in cultivationToSync) {
          const value = cultivationToSync[key];
          if (typeof value === 'string' && value.startsWith('file_')) {
            fileFields.push({ field: key, fileKey: value });
          }
        }

        // Process files if any
        if (fileFields.length > 0) {
          for (const { field, fileKey } of fileFields) {
            try {
              // Get file from DexieDB
              const file = await offlineDBService.getFile(fileKey);

              if (!file) continue;

              // Get upload config from form definition
              const fieldConfig = Array.isArray(cultivationFormDef?.formData)
                ? cultivationFormDef.formData.find(f => f.id === field)
                : null;

              // Convert to File object
              const fileObj = dataURLtoFile(
                file.dataUrl,
                file.fileName,
                file.fileType
              );

              // Create fake event
              const fakeEvent = { target: { files: [fileObj] } };

              // Create upload metadata
              const uploadMetadata = {
                id: field,
                uploadConfig: fieldConfig?.uploadConfig || {
                  maxFileSizeMB: 5,
                  uploadEndpoint: '/utility/upload/image'
                }
              };

              // Upload file
              try {
                const uploadResult = await FileUploader(
                  fakeEvent,
                  uploadMetadata
                );

                // Update cultivation data with file URL
                if (uploadResult && uploadResult.file) {
                  cultivationToSync[field] = uploadResult.file;

                  // Emit file uploaded event
                  syncEvents.emit(SYNC_EVENTS.FILE_UPLOADED, {
                    entityType: 'cultivation',
                    entityId: cultivation.id,
                    field,
                    fileKey,
                    uploadedUrl: uploadResult.file
                  });
                }
              } catch (uploadError) {
                console.error(`Error uploading file:`, uploadError);

                // Check for auth error during file upload
                if (isAuthError(uploadError)) {
                  authErrorOccurred = true;
                  syncEvents.emit(SYNC_EVENTS.AUTH_ERROR, {
                    error: uploadError
                  });
                  break;
                }
              }
            } catch (fileError) {
              console.error(`Error processing file:`, fileError);
            }
          }
        }

        // If auth error occurred during file processing, break out
        if (authErrorOccurred) break;

        try {
          // Log the cultivation data we're trying to sync for debugging
          console.log(
            `Attempting to sync cultivation ${cultivation.id}:`,
            cultivationToSync
          );

          // Submit cultivation to API - using the farmId parameter
          const response = await cultivationApi.submitCultivation(
            cultivationToSync,
            cultivationToSync.farm
          );

          // Log the raw response for debugging
          console.log(
            `API response for cultivation ${cultivation.id}:`,
            response
          );

          // Success check - similar to farm sync
          const isSuccessful =
            response &&
            (response.success === true ||
              (response.status >= 200 &&
                response.status < 300 &&
                !response.message &&
                !response.error));

          // Log the result of our success check
          console.log(
            `Sync success check for cultivation ${cultivation.id}: ${isSuccessful}`
          );

          if (isSuccessful) {
            // Get the permanent cultivation ID from the API response if available
            const permanentCultivationId =
              response.data?.id ||
              response.id ||
              response.cultivationId ||
              (response.data && response.data.cultivationId);

            // Only if successful, delete from local DB and associated files
            try {
              // Mark as synced in database AND remove from database
              const deleteResult = await offlineDBService.markAsSyncedAndRemove(
                'cultivations',
                cultivation.id
              );

              if (deleteResult) {
                console.log(
                  `Removed cultivation ${cultivation.id} from local DB after successful sync`
                );

                // Delete files only after successful sync
                for (const { fileKey } of fileFields) {
                  try {
                    await offlineDBService.deleteFile(fileKey);
                  } catch (error) {
                    console.error(`Error deleting file:`, error);
                  }
                }

                successCount++;

                // Emit cultivation synced event
                syncEvents.emit(SYNC_EVENTS.CULTIVATION_SYNCED, {
                  id: cultivation.id,
                  farmId: cultivationToSync.farm,
                  permanentId: permanentCultivationId || cultivation.id,
                  success: true
                });
              } else {
                console.error(
                  `Failed to delete cultivation ${cultivation.id} from database, marking as sync failure`
                );
                failCount++;

                // Emit cultivation synced event with failure
                syncEvents.emit(SYNC_EVENTS.CULTIVATION_SYNCED, {
                  id: cultivation.id,
                  farmId: cultivationToSync.farm,
                  success: false,
                  error: 'Failed to delete from local database'
                });
              }
            } catch (deleteError) {
              console.error(
                `Error deleting cultivation ${cultivation.id} after successful sync:`,
                deleteError
              );
              // If we can't delete, count as failure because data is still in local DB
              failCount++;

              // Emit cultivation synced event with failure
              syncEvents.emit(SYNC_EVENTS.CULTIVATION_SYNCED, {
                id: cultivation.id,
                farmId: cultivationToSync.farm,
                success: false,
                error:
                  deleteError.message || 'Error deleting from local database'
              });
            }
          } else {
            // Handle error response
            const errorMessage =
              response?.message ||
              response?.error ||
              response?.errorMessage ||
              (response?.data && response.data.message) ||
              'Unknown error';

            console.error(
              `Failed to sync cultivation ${cultivation.id}: ${errorMessage}`
            );

            // Update cultivation record with error info
            await offlineDBService.updateCultivationSyncError(
              cultivation.id,
              errorMessage
            );

            failCount++;

            // Emit cultivation synced event with failure
            syncEvents.emit(SYNC_EVENTS.CULTIVATION_SYNCED, {
              id: cultivation.id,
              farmId: cultivationToSync.farm,
              success: false,
              error: errorMessage
            });
          }
        } catch (apiError) {
          console.error(`API error syncing cultivation:`, apiError);

          // Check if this is an authentication error
          if (isAuthError(apiError)) {
            authErrorOccurred = true;
            syncEvents.emit(SYNC_EVENTS.AUTH_ERROR, { error: apiError });
            break;
          }

          // Extract error message from API error
          const errorMessage =
            apiError.response?.data?.desc ||
            apiError.response?.data?.message ||
            apiError.message ||
            'API error';

          // Update error info
          await offlineDBService.updateCultivationSyncError(
            cultivation.id,
            errorMessage
          );

          failCount++;

          // Emit cultivation synced event with failure
          syncEvents.emit(SYNC_EVENTS.CULTIVATION_SYNCED, {
            id: cultivation.id,
            farmId: cultivationToSync.farm,
            success: false,
            error: errorMessage
          });
        }
      } catch (error) {
        console.error(`Error syncing cultivation:`, error);
        failCount++;

        // Emit cultivation synced event with failure
        syncEvents.emit(SYNC_EVENTS.CULTIVATION_SYNCED, {
          id: cultivation.id,
          success: false,
          error: error.message || 'Unexpected error'
        });
      }
    }

    //===========================================================================
    // PHASE 6: HANDLE FORM SUBMISSIONS (IF ANY)
    //===========================================================================
    // Process forms if needed
    if (unsyncedData.forms.length > 0) {
      console.log('PHASE 6: PROCESSING FORM SUBMISSIONS');
      syncEvents.emit(SYNC_EVENTS.PROGRESS, {
        phase: 6,
        message: 'Processing form submissions',
        total: unsyncedData.forms.length,
        current: 0
      });

      // Form submission code would go here
      // For simplicity, just counting them as failed for now
      failCount += unsyncedData.forms.length;

      syncEvents.emit(SYNC_EVENTS.PROGRESS, {
        phase: 6,
        message: 'Processed form submissions',
        total: unsyncedData.forms.length,
        current: unsyncedData.forms.length
      });
    }

    // CRITICAL: Do a fresh check for unsynced data to get accurate state
    const finalUnsyncedData = await offlineDBService.getUnsyncedData();
    const stillHasUnsyncedData =
      finalUnsyncedData.farmers?.length > 0 ||
      finalUnsyncedData.farms?.length > 0 ||
      finalUnsyncedData.forms?.length > 0 ||
      finalUnsyncedData.cultivations?.length > 0;

    console.log('After sync, unsynced data check:', {
      farmersCount: finalUnsyncedData.farmers?.length || 0,
      farmsCount: finalUnsyncedData.farms?.length || 0,
      formsCount: finalUnsyncedData.forms?.length || 0,
      cultivationsCount: finalUnsyncedData.cultivations?.length || 0,
      stillHasUnsyncedData
    });

    // Get notification info
    const notification = showSyncNotification({
      successCount,
      failCount
    });

    // Create the final result object
    const result = {
      success: failCount === 0 && successCount > 0,
      successCount,
      failCount,
      stillHasUnsyncedData,
      notification,
      countsByType: {
        farmers: finalUnsyncedData.farmers?.length || 0,
        farms: finalUnsyncedData.farms?.length || 0,
        forms: finalUnsyncedData.forms?.length || 0,
        cultivations: finalUnsyncedData.cultivations?.length || 0
      },
      farmerIdMappings: Object.fromEntries(farmerIdMappings),
      farmIdMappings: Object.fromEntries(farmIdMappings)
    };

    // Emit completed event
    syncEvents.emit(SYNC_EVENTS.COMPLETED, result);
    globalEventBus.emit(GLOBAL_EVENTS.SYNC_COMPLETED, result);

    // Also emit database changed event
    globalEventBus.emit(GLOBAL_EVENTS.DB_CHANGED);

    // Return sync results
    return result;
  } catch (err) {
    console.error('Error in syncData function:', err);

    // Try to check unsynced data even if there was an error
    try {
      const checkData = await offlineDBService.getUnsyncedData();
      const stillHasUnsyncedData =
        checkData.farmers?.length > 0 ||
        checkData.farms?.length > 0 ||
        checkData.forms?.length > 0 ||
        checkData.cultivations?.length > 0;

      const result = {
        success: false,
        error: err.message || 'Unknown error in sync process',
        stillHasUnsyncedData,
        countsByType: {
          farmers: checkData.farmers?.length || 0,
          farms: checkData.farms?.length || 0,
          forms: checkData.forms?.length || 0,
          cultivations: checkData.cultivations?.length || 0
        }
      };

      // Emit failed event
      syncEvents.emit(SYNC_EVENTS.FAILED, result);
      globalEventBus.emit(GLOBAL_EVENTS.SYNC_FAILED, result);

      return result;
    } catch (checkErr) {
      console.error('Error checking unsynced data after sync error:', checkErr);

      // If we can't even check the database, return a generic error
      const result = {
        success: false,
        error: err.message || 'Unknown error in sync process',
        stillHasUnsyncedData: true
      };

      // Emit failed event
      syncEvents.emit(SYNC_EVENTS.FAILED, result);
      globalEventBus.emit(GLOBAL_EVENTS.SYNC_FAILED, result);

      return result;
    }
  }
};

export default {
  syncData,
  isAuthError,
  dataURLtoFile,
  showSyncNotification,
  syncEvents,
  SYNC_EVENTS
};
